import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, Share, Plus, MoreVertical, MoreHorizontal, Search, ChevronRight, Check, Download } from "lucide-react";

const FRAME_MS = 2400;

// Golden tap indicator with pulsing rings
function TapDot({ x, y }) {
  return (
    <div className="pointer-events-none absolute z-30" style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}>
      {[0, 0.4].map((d) => (
        <motion.span
          key={d}
          className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#E5C158]"
          initial={{ scale: 0.4, opacity: 0.9 }}
          animate={{ scale: 1.9, opacity: 0 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut", delay: d }}
        />
      ))}
      <motion.span
        className="relative block h-6 w-6 rounded-full bg-[#E5C158] shadow-[0_0_14px_rgba(229,193,88,0.7)]"
        animate={{ scale: [1, 0.82, 1] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

function StatusBar() {
  return (
    <div className="flex items-center justify-between px-3 pt-1.5 text-[9px] font-semibold text-[#E4E4E7]">
      <span>9:41</span>
      <div className="flex items-center gap-1">
        <span className="inline-block h-2 w-3 rounded-[2px] border border-[#8A8E96]" />
        <span className="inline-block h-2 w-1.5 rounded-[1px] bg-[#8A8E96]" />
      </div>
    </div>
  );
}

function AppIcon({ size = 40 }) {
  return (
    <div
      className="flex items-center justify-center rounded-[22%] bg-gradient-to-br from-[#E5C158] to-[#8A6A17] shadow-lg"
      style={{ width: size, height: size }}
    >
      <span className="tabular-nums font-semibold font-bold text-[#0A0A0C]" style={{ fontSize: size * 0.32 }}>C2G</span>
    </div>
  );
}

// A mock page showing the app open in the browser
function BrowserPage() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
      <AppIcon size={54} />
      <p className="text-sm text-[#F4F4F5] font-semibold">Coach2Go</p>
      <p className="text-[9px] leading-tight text-[#8A8E96]">Il tuo COACH sempre in tasca</p>
      <div className="mt-1 h-1.5 w-16 rounded-full bg-[#E5C158]/60" />
      <div className="h-1.5 w-24 rounded-full bg-[#27272A]" />
      <div className="h-1.5 w-20 rounded-full bg-[#27272A]" />
    </div>
  );
}

function MenuRow({ icon: Icon, label, active }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${active ?"bg-[#E5C158]/15 ring-1 ring-[#E5C158]/60" : ""}`}>
      <Icon size={15} className={active ? "text-[#E5C158]" : "text-[#B4B4BC]"} />
      <span className={`flex-1 text-left text-[12px] ${active ?"font-semibold text-[#F4F4F5]" : "text-[#D4D4D8]"}`}>{label}</span>
      {active && <ChevronRight size={13} className="text-[#E5C158]" />}
    </div>
  );
}

/* ---------- iOS frames ---------- */
function iosFrames() {
  return [
    {
      caption: "1. Tocca i tre puntini «···» in basso a destra",
      tap: { x: 88, y: 91 },
      render: (
        <div className="relative flex h-full flex-col">
          <StatusBar />
          <div className="flex-1"><BrowserPage /></div>
          {/* Safari bottom bar */}
          <div className="flex items-center gap-2 border-t border-[#27272A] bg-[#161619] px-2.5 py-2">
            <div className="flex flex-1 items-center gap-1.5 rounded-lg bg-[#0D0D0F] px-2.5 py-1.5">
              <Search size={10} className="text-[#8A8E96]" />
              <span className="text-[9px] text-[#8A8E96]">coach2go.app</span>
            </div>
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#E5C158]/15 ring-1 ring-[#E5C158]/60">
              <MoreHorizontal size={15} className="text-[#E5C158]" />
            </div>
          </div>
        </div>
      ),
    },
    {
      caption: "2. Tocca «Condividi»",
      tap: { x: 50, y: 42 },
      render: (
        <div className="relative flex h-full flex-col">
          <StatusBar />
          <div className="flex-1"><BrowserPage /></div>
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }}
            className="absolute inset-x-2 bottom-2 space-y-1 rounded-2xl border border-[#27272A] bg-[#18181C] p-2 shadow-2xl"
          >
            <MenuRow icon={Share} label="Condividi" active />
            <MenuRow icon={Plus} label="Nuova scheda" />
            <MenuRow icon={Search} label="Trova nella pagina" />
          </motion.div>
        </div>
      ),
    },
    {
      caption: "3. Scegli «Aggiungi alla schermata Home»",
      tap: { x: 50, y: 55 },
      render: (
        <div className="relative flex h-full flex-col">
          <StatusBar />
          <div className="flex-1 opacity-40"><BrowserPage /></div>
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }}
            className="absolute inset-x-2 bottom-2 rounded-2xl border border-[#27272A] bg-[#18181C] p-2 shadow-2xl"
          >
            <div className="flex items-center gap-2 rounded-xl bg-[#0D0D0F] p-2">
              <AppIcon size={30} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12px] font-semibold text-[#F4F4F5]">Coach2Go</p>
                <p className="truncate text-[8px] text-[#8A8E96]">coach2go.app</p>
              </div>
            </div>
            <div className="mt-1.5 space-y-1">
              <MenuRow icon={Plus} label="Aggiungi alla schermata Home" active />
              <MenuRow icon={Search} label="Aggiungi ai segnalibri" />
            </div>
          </motion.div>
        </div>
      ),
    },
    {
      caption: "4. Tocca «Aggiungi» in alto a destra",
      tap: { x: 87, y: 12 },
      render: (
        <div className="relative flex h-full flex-col bg-[#0D0D0F]">
          <StatusBar />
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-[12px] text-[#8A8E96]">Annulla</span>
            <span className="text-[12px] font-semibold text-[#F4F4F5]">Home</span>
            <span className="rounded-md bg-[#E5C158]/15 px-2 py-0.5 text-[12px] font-bold text-[#E5C158] ring-1 ring-[#E5C158]/60">Aggiungi</span>
          </div>
          <div className="mt-6 flex flex-col items-center gap-3 px-4">
            <AppIcon size={56} />
            <div className="w-full rounded-lg border border-[#27272A] bg-[#161619] px-3 py-2 text-[12px] text-[#F4F4F5]">Coach2Go</div>
            <p className="text-center text-[9px] leading-tight text-[#8A8E96]">Un'icona verrà aggiunta alla schermata Home.</p>
          </div>
        </div>
      ),
    },
    {
      caption: "Fatto! Coach2Go è sul telefono ✓",
      tap: null,
      render: <HomeScreen />,
    },
  ];
}

/* ---------- Android frames ---------- */
function androidFrames() {
  return [
    {
      caption: "1. Tocca i tre puntini «⋮» in alto a destra",
      tap: { x: 90, y: 8 },
      render: (
        <div className="relative flex h-full flex-col">
          <StatusBar />
          <div className="flex items-center gap-2 border-b border-[#27272A] bg-[#161619] px-2.5 py-2">
            <div className="flex flex-1 items-center gap-1.5 rounded-full bg-[#0D0D0F] px-2.5 py-1.5">
              <Search size={10} className="text-[#8A8E96]" />
              <span className="text-[9px] text-[#8A8E96]">coach2go.app</span>
            </div>
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#E5C158]/15 ring-1 ring-[#E5C158]/60">
              <MoreVertical size={15} className="text-[#E5C158]" />
            </div>
          </div>
          <div className="flex-1"><BrowserPage /></div>
        </div>
      ),
    },
    {
      caption: "2. Tocca «Installa app»",
      tap: { x: 72, y: 30 },
      render: (
        <div className="relative flex h-full flex-col">
          <StatusBar />
          <div className="flex items-center gap-2 border-b border-[#27272A] bg-[#161619] px-2.5 py-2">
            <div className="flex flex-1 items-center gap-1.5 rounded-full bg-[#0D0D0F] px-2.5 py-1.5">
              <span className="text-[9px] text-[#8A8E96]">coach2go.app</span>
            </div>
            <MoreVertical size={15} className="text-[#B4B4BC]" />
          </div>
          <div className="flex-1 opacity-40"><BrowserPage /></div>
          <motion.div
            initial={{ scale: 0.9, opacity: 0, originX: 1, originY: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.28 }}
            className="absolute right-2 top-11 w-40 space-y-0.5 rounded-xl border border-[#27272A] bg-[#18181C] p-1.5 shadow-2xl"
          >
            <MenuRow icon={Search} label="Nuova scheda" />
            <MenuRow icon={Download} label="Installa app" active />
            <MenuRow icon={Plus} label="Aggiungi a Home" />
          </motion.div>
        </div>
      ),
    },
    {
      caption: "3. Conferma «Installa»",
      tap: { x: 74, y: 63 },
      render: (
        <div className="relative flex h-full flex-col bg-black/40">
          <StatusBar />
          <div className="flex flex-1 items-center justify-center px-3">
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", damping: 22, stiffness: 280 }}
              className="w-full rounded-2xl border border-[#27272A] bg-[#18181C] p-3.5 shadow-2xl"
            >
              <div className="flex items-center gap-2.5">
                <AppIcon size={34} />
                <div>
                  <p className="text-[12px] font-semibold text-[#F4F4F5]">Installa app</p>
                  <p className="text-[9px] text-[#8A8E96]">Coach2Go</p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <span className="text-[12px] font-medium text-[#8A8E96]">Annulla</span>
                <span className="rounded-full bg-[#E5C158]/15 px-3 py-1 text-[12px] font-bold text-[#E5C158] ring-1 ring-[#E5C158]/60">Installa</span>
              </div>
            </motion.div>
          </div>
        </div>
      ),
    },
    {
      caption: "Fatto! Coach2Go è sul telefono ✓",
      tap: null,
      render: <HomeScreen />,
    },
  ];
}

function HomeScreen() {
  return (
    <div className="relative flex h-full flex-col bg-gradient-to-b from-[#1a1a22] to-[#0A0A0C]">
      <StatusBar />
      <div className="mt-4 grid grid-cols-4 gap-x-3 gap-y-4 px-4">
        {Array.from({ length: 8 }).map((_, i) =>
          i === 5 ? (
            <div key={i} className="flex flex-col items-center gap-1">
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", damping: 12, stiffness: 220, delay: 0.15 }}
                className="relative"
              >
                <AppIcon size={36} />
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
                  className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#3FB950] ring-2 ring-[#0A0A0C]"
                >
                  <Check size={9} className="text-white" strokeWidth={3} />
                </motion.span>
              </motion.div>
              <span className="text-[7px] text-[#D4D4D8]">Coach2Go</span>
            </div>
          ) : (
            <div key={i} className="flex flex-col items-center gap-1">
              <div className="h-9 w-9 rounded-[22%] bg-[#27272A]" />
              <span className="h-1 w-6 rounded-full bg-[#27272A]/60" />
            </div>
          )
        )}
      </div>
    </div>
  );
}

export function InstallAnimation({ device = "ios", className = "" }) {
  const frames = device === "ios" ? iosFrames() : androidFrames();
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    setStep(0);
    setPlaying(true);
  }, [device]);

  useEffect(() => {
    if (!playing) return;
    timer.current = setInterval(() => {
      setStep((s) => (s + 1) % frames.length);
    }, FRAME_MS);
    return () => clearInterval(timer.current);
  }, [playing, frames.length]);

  const replay = () => {
    setStep(0);
    setPlaying(true);
  };

  const current = frames[step];

  return (
    <div className={`flex flex-col items-center ${className}`} data-testid={`install-animation-${device}`}>
      {/* Phone frame */}
      <div className="relative rounded-[2.2rem] border-[5px] border-[#2A2A30] bg-[#0A0A0C] p-1 shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
        <div className="absolute left-1/2 top-1 z-40 h-4 w-16 -translate-x-1/2 rounded-full bg-[#0A0A0C]" />
        <div className="relative h-[380px] w-[210px] overflow-hidden rounded-[1.8rem] bg-[#0D0D0F]">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${device}-${step}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="absolute inset-0"
            >
              {current.render}
            </motion.div>
          </AnimatePresence>
          {current.tap && <TapDot x={current.tap.x} y={current.tap.y} />}
        </div>
      </div>

      {/* Caption */}
      <div className="mt-4 flex min-h-[40px] items-center px-2">
        <AnimatePresence mode="wait">
          <motion.p
            key={`cap-${device}-${step}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="text-center text-sm font-medium leading-snug text-[#E8E4D8]"
            data-testid="install-animation-caption"
          >
            {current.caption}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Progress dots */}
      <div className="mt-3 flex items-center gap-1.5">
        {frames.map((_, i) => (
          <button
            key={i}
            onClick={() => { setStep(i); }}
            data-testid={`install-animation-dot-${i}`}
            className={`h-1.5 rounded-full transition-all ${i === step ?"w-5 bg-[#E5C158]" : "w-1.5 bg-[#3A3A40]"}`}
            aria-label={`Passo ${i + 1}`}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={() => setPlaying((p) => !p)}
          data-testid="install-animation-playpause"
          className="flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-2 text-sm font-semibold text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/20"
        >
          {playing ? <Pause size={15} /> : <Play size={15} />}
          {playing ? "Pausa" : "Riproduci"}
        </button>
        <button
          onClick={replay}
          data-testid="install-animation-replay"
          className="flex items-center gap-2 rounded-full border border-[#27272A] bg-[#121215] px-4 py-2 text-sm font-medium text-[#B4B4BC] transition-colors hover:text-[#F4F4F5]"
        >
          <RotateCcw size={15} /> Rivedi
        </button>
      </div>
    </div>
  );
}
